import React from 'react';
import { useNavigate } from 'react-router-dom';
import yardrayLogo from '../assets/yardray-logo.png';
import './LandingPage.css';

function LandingPage({ onGetStarted }) {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (onGetStarted) {
      onGetStarted();
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="landing-page">

      <header className="landing-hero">
        <div className="landing-hero-login-wrap">
          <button className="landing-login-btn" onClick={handleGetStarted}>
            Login / Sign Up
          </button>
        </div>
        <div className="landing-hero-inner">
          <img src={yardrayLogo} alt="Yardray" className="landing-logo" />
          <p className="landing-tagline">Your yard's permanent memory</p>
          <p className="landing-description">
            Track plants, get AI-powered care advice, stay on top of lawn treatments,
            and never miss a watering day with smart weather automation.
          </p>
          <button className="landing-start-btn" onClick={handleGetStarted}>
            Start Free Today
          </button>
          <p className="landing-free-note">Free to use - more features being added regularly</p>
        </div>
      </header>

      <section className="landing-story">
        <div className="landing-story-inner">
          <h2>Why We Built This</h2>
          <p>
            A couple of years ago we moved into a new house with the most beautiful yard we had ever seen.
            The previous owner clearly lived outside — gorgeous hydrangeas, flowering trees, bushes we
            couldn't name, and beds full of plants we had never dealt with before. It was stunning.
            And we had absolutely no idea what to do with any of it.
          </p>
          <p>
            We're not full-time gardeners. We have weekends. Every time we walked outside we had more
            questions than answers. We wanted one place to point a phone at a plant, find out what it is,
            and get real answers — when does it bloom, does it come back every year, when do we prune it?
          </p>
          <p>
            Then came the lawn. We started tracking what was applied, when, and with what product.
            We wanted to know if it was working and what to skip. Yardray is what we built to solve
            all of this. It's not for professional gardeners. It's for people who inherited a beautiful
            yard, want to take care of it, and just need a straight answer without spending an hour
            researching before they can pick up a shovel.
          </p>
          <div className="landing-story-cta">
            <button className="landing-try-btn" onClick={handleGetStarted}>Try Yardray Free</button>
          </div>
        </div>
      </section>

      <section className="landing-features">
        <h2>Everything You Need to Track Your Yard</h2>
        <div className="landing-features-grid">
          {[
            { icon: '📸', title: 'Photo Timeline', desc: 'Track your plants growth with photos. Watch tomatoes ripen, flowers bloom, and trees mature over time.' },
            { icon: '🔍', title: 'AI Plant Identification', desc: 'Do not know what plant you have? Upload a photo and get instant AI-powered identification.' },
            { icon: '🌦️', title: 'Weather Alerts', desc: 'Weather alerts on your dashboard. Frost warnings for tender plants. Rain and watering guidance built in.' },
            { icon: '✅', title: 'Task Management', desc: 'Create tasks for watering, fertilizing, and pruning. Never forget when you last fed your rose bushes.' },
            { icon: '💬', title: 'AI Yard Assistant', desc: 'Ask questions about plant care, pest control, or when to harvest. Get instant advice for your yard.' },
            { icon: '🌿', title: 'Lawn Care Tracking', desc: 'Track fertilizer applications, weed treatments, and lawn products. Know exactly what was applied, when, and whether it worked. Stop guessing what the lawn service did last season.' },
          ].map(({ icon, title, desc }) => (
            <div key={title} className="landing-feature-card">
              <div className="landing-feature-icon">{icon}</div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-pricing">
        <div className="landing-pricing-inner">
          <h2>Start Free Today</h2>
          <p className="landing-pricing-sub">Everything you need to track your yard. No credit card required.</p>
          <div className="landing-pricing-card">
            <h3>Free Forever</h3>
            <div className="landing-price">$0</div>
            <p className="landing-pricing-note">No credit card required.</p>
            <ul className="landing-pricing-list">
              <li>Track plants</li>
              <li>Upload plant photos</li>
              <li>AI plant identification</li>
              <li>Weather alerts and frost warnings</li>
              <li>Task management</li>
              <li>AI yard assistant</li>
              <li>Your data stays private</li>
            </ul>
            <button className="landing-get-started-btn" onClick={handleGetStarted}>
              Get Started Free
            </button>
            <p className="landing-more-note">More features being added regularly.</p>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-links">
            <a href="/terms">Terms of Service</a>
            <a href="/privacy">Privacy Policy</a>
            <a href="mailto:hello@yardray.app">Contact</a>
          </div>
          <p className="landing-footer-copy">
            © {new Date().getFullYear()} Yardray. Made with love for yard enthusiasts everywhere.
          </p>
        </div>
      </footer>

    </div>
  );
}

export default LandingPage;
